import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from "react-resizable-panels";
import {
  AddCircleOutlineRounded,
  AddToDrive,
  ArrowDropDown,
  Check,
  Close,
  LabelOutlined,
  Print,
  SaveAlt,
  Share,
  Visibility,
} from "@mui/icons-material";

import {
  useAttachments,
  useBreakpoint,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
  useUser,
} from "hooks";

import { Comment, History } from '@mui/icons-material';
import DialogHeader from "./DialogHeader";
import { getTranslation } from "common";
import CommentsSection, {
  Comment as TSXComment,
  CompleteComment,
} from "components/CommentsSection";
import { flownodesApi, organizationsApi, processesApi, statisticsApi } from "api";
import {
  buildTaskDetailDialogPreviewData,
  getFileExtension,
  getLabelDataFromJsonTags,
  getUserData,
  taskDetailDialogExport,
  toBase64,
} from "common/utilities";
import AttachmentList from "components/AttachmentList";
import { Priority } from "hooks/usePriorities";
import { SequenceFlow, Task } from "model/activitiesModel";
import LabelManager from "../labels/LabelManager";
import FieldsList from "components/FieldsList";
import Labels from "components/labels/Labels";
import { UserData } from "hooks/useUserDataKeys";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate } from "react-router-dom";
import HistoriesSection, { HistoryEntry } from "components/HistoriesSection";
import GoogleDriveMenu from "components/menus/GoogleDriveMenu";
import ProcessNavigationMenu from "components/menus/ProcessNavigationMenu";
import DropZone from "components/DropZone";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { RichTextEditor } from "components/Editor/plugins/RichTextEditor";
import type { MDXEditorMethods } from "@mdxeditor/editor";
import DOMPurify from "dompurify";
import ConfirmDialog from "./ConfirmDialog";


interface BaseTaskDetailDialogProps {
  open: boolean;
  doing: boolean;
  onClose: () => void;
  nextButtonLabel?: string;
  undoButtonLabel?: string;
  handleNextStep?: (task: Task) => void;
  handleBackStep?: (task: Task) => void;
  handleSequenceFlowChoice?: (task: Task, sequenceFlow: SequenceFlow) => void;
  administrator?: boolean;
  task?: Task;
  readonly?: boolean;
  historyMode?: boolean;
  onPriorityUpdate?: (taskId: number, newPriority: Priority) => void;
  refresh?: () => void;
}

const TaskDetailDialog: React.FC<BaseTaskDetailDialogProps> = ({
  open,
  doing,
  onClose,
  task,
  readonly = false,
  handleSequenceFlowChoice,
  handleNextStep,
  handleBackStep,
  nextButtonLabel,
  undoButtonLabel,
  administrator,
  onPriorityUpdate,
  refresh,
  historyMode = false,
}) => {
  // hooks & state
  const user = useUser();
  const descriptionDetailRef = useRef<MDXEditorMethods>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    storageDiskCanUse,
    bypassSubscription,
    checkLoading,
    refreshSubscriptionFeatures,
    storageUsage,
  } = useSubscriptionFeatures({ enabled: open });
  const navigate = useNavigate();
  const menuOpen = Boolean(anchorEl);

  // snackbar, translations, organization
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const isMobile = useBreakpoint("down", "sm");
  const selectedOrganization = useSelectedOrganization();

  // Persist panel layout in localStorage
  const LAYOUT_KEY = "taskdetail-layout";
  const loadLayout = (key: string): { [id: string]: number } | undefined => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : undefined; } catch { return undefined; }
  };
  const saveLayout = (key: string, layout: { [id: string]: number }) => {
    try { localStorage.setItem(key, JSON.stringify(layout)); } catch { }
  };
  let organizationId: number | null | undefined = null;
  if (selectedOrganization) {
    organizationId = selectedOrganization.id;
  }

  // data state
  const [comments, setComments] = useState<CompleteComment[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [attachments, , getAttachments, images, histories, loadingAttachments] = useAttachments(task || null);
  const [uploadAttachmentError, setUploadAttachmentError] = useState(false);
  const [maxAttachmentsError, setMaxAttachmentsError] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [allPriorities, setAllPriorities] = useState<Priority[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const [isLabelManagerShown, setIsLabelManagerShown] = useState<HTMLElement | null>(null);
  const [userdataEntries, setUserDataEntries] = useState<{ [key: string]: UserData }>({});
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const [activeView, setActiveView] = useState<'histories' | 'comments'>('comments');
  const [descriptionDetail, setDescriptionDetail] = useState<string | null>(null);

  // Inline editing state
  const [editingProcessName, setEditingProcessName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedProcessName, setEditedProcessName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [isDescriptionDetailFocused, setIsDescriptionDetailFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [fullContentDialog, setFullContentDialog] = useState({
    open: false,
    title: "",
    content: "",
  });

  // Determine if user can edit process details (admin or process starter)
  const isProcessStarter = useMemo(() => {
    if (!user || !history || history.length === 0) return false;
    const startEntry = history[0];
    const owner = startEntry?.owner?.trim();
    if (!owner) return false;
    const userFullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
    return owner === userFullName || owner === user.email || owner === user.username;
  }, [user, history]);

  const canEditProcessDetails = administrator || isProcessStarter;

  // Reset state when task changes to avoid showing stale data from previous card
  useEffect(() => {
    setComments([]);
    setHistory([]);
    setShowMore(false);
    setUserDataEntries({});
    setSelectedPriority(null);
    setActiveView('comments');
    setDescriptionDetail(null);
    setEditingProcessName(false);
    setEditingDescription(false);
  }, [task?.idprocesstoken]);

  // helper toggles
  const toggleShowMore = () => setShowMore((c) => !c);
  const handleLabelManagerClose = () => setIsLabelManagerShown(null);
  const closeFullContentDialog = () => {
    setFullContentDialog({ open: false, title: "", content: "" });
  };

  // Inline editing handlers
  const startEditProcessName = () => {
    setEditedProcessName(task?.processmodelname || "");
    setEditingProcessName(true);
  };
  const startEditDescription = () => {
    setEditedDescription(task?.description || "");
    setEditingDescription(true);
  };
  const cancelEditProcessName = () => setEditingProcessName(false);
  const cancelEditDescription = () => setEditingDescription(false);

  const saveProcessName = async () => {
    if (!task || !editedProcessName.trim()) return;
    setSaving(true);
    try {
      await processesApi.put(`/${task.idprocess}`, { processmodelname: editedProcessName.trim() });
      task.processmodelname = editedProcessName.trim();
      setEditingProcessName(false);
      sendSnack({ type: "success", message: getTranslation(translations, "pm.button.save") });
      refresh?.();
    } catch {
      // sendSnack({ type: "error", message: getTranslation(translations, "generic.error") || "Error" });
    } finally {
      setSaving(false);
    }
  };

  const saveDescription = async () => {
    if (!task) return;
    setSaving(true);
    try {
      await processesApi.put(`/${task.idprocess}`, { description: editedDescription.trim() });
      task.description = editedDescription.trim();
      setEditingDescription(false);
      sendSnack({ type: "success", message: getTranslation(translations, "pm.button.save") });
      refresh?.();
    } catch {
      // sendSnack({ type: "error", message: getTranslation(translations, "generic.error") || "Error" });
    } finally {
      setSaving(false);
    }
  };

  const saveDescriptionDetail = async () => {
    if (!task) return;
    const markdown = descriptionDetailRef.current?.getMarkdown();
    if (markdown === undefined) return;
    try {
      await processesApi.put(`/${task.idprocess}`, { descriptiondetail: markdown });
      setDescriptionDetail(markdown);
      refresh?.();
    } catch {
      // ignore
    }
  };

  const buildAttachmentPreviews = () => {
    if (!task) return [];
    return attachments
      .map((attachment) => {
        const extension = getFileExtension(attachment.filename);
        const image = images[attachment.id];
        if (!extension || !image) {
          return null;
        }

        const mimeTypeByExtension: Record<string, string> = {
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          webp: "image/webp",
        };
        const mimeType = mimeTypeByExtension[extension];
        if (!mimeType) {
          return null;
        }

        return {
          name: attachment.filename,
          dataUrl: image,
          mimeType,
        };
      })
      .filter((preview): preview is { name: string; dataUrl: string; mimeType: string } => preview !== null);
  };

  const handleDownloadTaskDocument = async () => {
    if (!task) return;

    setDownloadingDocument(true);
    try {
      taskDetailDialogExport({
        task,
        selectedPriorityName: selectedPriority?.name,
        userdataEntries,
        yesLabel: getTranslation(translations, "generic.yes.caption"),
        attachmentPreviews: buildAttachmentPreviews(),
        organizationName: selectedOrganization?.name || "",
      });
    } catch {
    } finally {
      setDownloadingDocument(false);
    }
  };

  const handlePreviewTaskDocument = () => {
    if (!task) return;
    try {
      const blob = taskDetailDialogExport({
        task,
        selectedPriorityName: selectedPriority?.name,
        userdataEntries,
        yesLabel: getTranslation(translations, "generic.yes.caption"),
        attachmentPreviews: buildAttachmentPreviews(),
        organizationName: selectedOrganization?.name || "",
        mode: "blob",
      });
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPreviewPdfUrl(url);
        setPreviewOpen(true);
      }
    } catch {
      // sendSnack({
      //   type: "error",
      //   message: getTranslation(translations, "generic.error") || "Error",
      // });
    }
  };

  const handleShareTaskDocument = async () => {
    if (!task) return;

    try {
      const blob = taskDetailDialogExport({
        task,
        selectedPriorityName: selectedPriority?.name,
        userdataEntries,
        yesLabel: getTranslation(translations, "generic.yes.caption"),
        attachmentPreviews: buildAttachmentPreviews(),
        organizationName: selectedOrganization?.name || "",
        mode: "blob",
      });

      if (!blob) return;

      const fileName = `${task.flownodename || task.name || "task-detail"}.pdf`.replace(/[<>:"/\\|?*]+/g, "-");
      const file = new File([blob], fileName, { type: "application/pdf" });
      if (!navigator.canShare?.({ files: [file] })) {
        sendSnack({
          type: "info",
          message: getTranslation(translations, "attachment.preview.label.not.available") || "Condivisione non supportata da questo browser.",
        });
        return;
      }

      await navigator.share({
        title: task.flownodename || task.name || "Documento task",
        files: [file],
      });
    } catch {
      // sendSnack({
      //   type: "error",
      //   message: getTranslation(translations, "generic.error") || "Error",
      // });
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    setPreviewOpen(false);
  };

  const getMobilePreviewImageSrc = (dataUrl: string, mimeType: string) =>
    dataUrl.startsWith("data:") ? dataUrl : `data:${mimeType};base64,${dataUrl}`;

  const renderMobileTaskDocumentPreview = () => {
    if (!task) return null;

    const previewData = buildTaskDetailDialogPreviewData({
      task,
      selectedPriorityName: selectedPriority?.name,
      userdataEntries,
      yesLabel: getTranslation(translations, "generic.yes.caption"),
      attachmentPreviews: buildAttachmentPreviews(),
      organizationName: selectedOrganization?.name || "",
    });

    const renderRows = (rows: { label: string; value: string }[]) => (
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderBottom: 0,
          bgcolor: "background.paper",
        }}
      >
        {rows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: "grid",
              gridTemplateColumns: "38% 62%",
              borderBottom: 1,
              borderColor: "divider",
              minHeight: 42,
            }}
          >
            <Box sx={{ p: 1, borderRight: 1, borderColor: "divider", bgcolor: "grey.50" }}>
              <Typography variant="caption" fontWeight={700}>
                {row.label}
              </Typography>
            </Box>
            <Box sx={{ p: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {row.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );

    return (
      <Box sx={{ p: 2, bgcolor: "#f4f5f7", minHeight: "100%", overflow: "auto" }}>
        <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: "divider" }}>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              {selectedOrganization?.name || "Flowenti"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date().toLocaleString()}
            </Typography>
          </Stack>

          {previewData.documentRows.length > 0 && (
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Dati documento
              </Typography>
              {renderRows(previewData.documentRows)}
            </Stack>
          )}

          {previewData.userdataRows.length > 0 && (
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Dati personalizzati
              </Typography>
              {renderRows(previewData.userdataRows)}
            </Stack>
          )}

          {previewData.attachmentPreviews.length > 0 && (
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                Allegati
              </Typography>
              {previewData.attachmentPreviews.map((preview) => (
                <Box
                  key={preview.name}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    p: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                    {preview.name}
                  </Typography>
                  <Box
                    component="img"
                    src={getMobilePreviewImageSrc(preview.dataUrl, preview.mimeType)}
                    alt={preview.name}
                    sx={{ width: "100%", maxHeight: 360, objectFit: "contain", display: "block" }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    );
  };

  // Menu handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // fetch comments/history
  const getComments = useCallback(async () => {
    if (!task) return;
    try {
      const response = await processesApi.get(`${task.idprocess}/comments`);
      setComments([...response.data.comments]);
    } catch {
      // ignore
    }
  }, [task]);

  const getHistory = useCallback(async () => {
    if (!task) return;
    try {
      const response = await statisticsApi.get(`/process/${task.idprocesstoken}/tasks/history`);
      setHistory(response.data.data);
    } catch {
      // ignore
    }
  }, [task]);

  const onComment = async ({ comment }: TSXComment) => {
    if (task && comment) {
      try {
        await flownodesApi.post(`${task.idprocesstoken}/comments`, { comment });
        getComments();
      } catch {
        // ignore
      }
    }
  };

  const onEditComment = async (id: number, comment: string) => {
    if (task && comment) {
      try {
        await processesApi.put(`${task.idprocess}/comments/${id}`, { comment });
        getComments();
      } catch {
      }
    }
  };

  // upload local files
  const handleUploadFile = async (files: FileList | File[] | null) => {
    if (!task) return;
    let promises: Promise<any>[] = [];
    let hasError = false;
    try {
      if (files && files.length > 0) {
        const newFiles = Array.from(files);
        const fileSize = newFiles.reduce((acc, file) => acc + file.size, 0);
        const totalSize = (storageUsage?.using || 0) + fileSize;
        if (totalSize > (storageUsage?.permit || 0) && !bypassSubscription) {
          setUploadAttachmentError(true);
          return;
        }
        const totalAttachmentCount = attachments.length + newFiles.length;
        if (totalAttachmentCount > 10) {
          setMaxAttachmentsError(true);
          return;
        }
        for (let file of newFiles) {
          // Cross-session check: check if filename is already in the server-side attachments
          if (attachments.some(a => a.filename === file.name)) {
            sendSnack({
              message: `${getTranslation(translations, "attachment.already.present") || "File già presente"}: ${file.name}`,
              type: "info",
            });
            continue;
          }

          const payload = {


            idorganization: organizationId,
            filename: file.name,
            cloudlink: "",
            idtype: 1,
            description: file.name,
            blob: await toBase64(file),
            size: file.size,
          };
          promises.push(flownodesApi.post(`${task.idprocesstoken}/attachments`, payload));
        }
        try {
          await Promise.all(promises);
        } catch {
          hasError = true;
        }
        if (!hasError && promises.length > 0) {
          sendSnack({
            type: "success",
            message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
          });
        }
        refreshSubscriptionFeatures();
        getAttachments();
      }
      setUploadAttachmentError(false);
      setMaxAttachmentsError(false);
    } finally {
      // nothing
    }
  };

  const handleUploadFileRef = useRef(handleUploadFile);
  handleUploadFileRef.current = handleUploadFile;

  // priorities
  const fetchOrganizationPriorities = useCallback(async () => {
    if (!organizationId) return;
    try {
      const response = await organizationsApi.get(`/${organizationId}/priorities`);
      if (response && response.data && response.data.priorities) {
        setAllPriorities(response.data.priorities);
      }
    } catch {
      // ignore
    }
  }, [organizationId]);

  useEffect(() => {
    if (open) {
      fetchOrganizationPriorities();
    }
  }, [fetchOrganizationPriorities, open]);

  useEffect(() => {
    if (task && task.priority_name) {
      const priority = allPriorities.find((p) => p.name === task.priority_name);
      setSelectedPriority(priority || null);
    }
  }, [allPriorities, task]);

  const updatePriority = async (newPriority: Priority) => {
    if (!task) return;
    try {
      const { data } = await flownodesApi.put(`/${task.idprocess}/priorities/${newPriority.id}`, {});
      setSelectedPriority(newPriority);
      onPriorityUpdate && onPriorityUpdate(task.idprocesstoken, newPriority);
      sendSnack({ type: "success", message: data.responsemessage });
    } catch {
      // ignore
    }
  };

  const handlePriorityChange = (event: SelectChangeEvent<string | number>) => {
    const newPriority = allPriorities.find((priority) => priority.id === Number(event.target.value));
    if (newPriority) {
      updatePriority(newPriority);
    }
  };

  // userdata parsing
  useEffect(() => {
    const parsedUserData = getUserData(task?.userdata);
    const userDataEntries = Object.fromEntries(
      Object.entries(parsedUserData).map(([key, item]) => [key, item])
    );
    setUserDataEntries(userDataEntries);
    Object.entries(parsedUserData).forEach(([key, value]) => {
      if ((value as UserData).inCard) {
        localStorage.setItem(`userdata-per-${task?.idprocess}`, key);
      }
    });
  }, [task]);

  useEffect(() => {
    if (open) {
      getComments();
      getAttachments();
      getHistory();
      if (task?.idprocess) {
        processesApi.get(`/${task.idprocess}`).then(({ data }) => {
          const detail = data?.process?.descriptiondetail || data?.process?.description_detail || data?.descriptiondetail || data?.description_detail || null;
          if (detail) setDescriptionDetail(detail);
        }).catch(() => { });
      }
    }
    if (attachments.length <= 10) {
      setMaxAttachmentsError(false);
    }
  }, [getComments, getAttachments, open, attachments.length, task?.idprocess]);


  // RENDER
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ bgcolor: "#f4f5f7" }}>

          <DialogHeader onClose={onClose}>
            <Box sx={{ px: 2, width: "100%", }}>
              {task && (
                <Stack
                  direction={isMobile ? "column" : "row"}
                  alignItems={isMobile ? "stretch" : "center"}
                  spacing={isMobile ? 1 : 2}
                >
                  <Box flex={1} minWidth={0}>
                    {/* Title line: processmodelname (historyMode) or flownodename (normal) */}
                    {historyMode ? (
                      editingProcessName ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TextField
                            value={editedProcessName}
                            onChange={(e) => setEditedProcessName(e.target.value)}
                            variant="standard"
                            size="small"
                            autoFocus
                            sx={{ flex: 1 }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveProcessName();
                              if (e.key === "Escape") cancelEditProcessName();
                            }}
                            disabled={saving}
                          />
                          <IconButton size="small" onClick={saveProcessName} disabled={saving}>
                            <Check fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditProcessName} disabled={saving}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Box
                          onClick={canEditProcessDetails ? startEditProcessName : undefined}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25,
                            cursor: canEditProcessDetails ? 'pointer' : 'default',
                            '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                            transition: 'border-color 0.15s, background-color 0.15s',
                            display: 'inline-block',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                            {task.processmodelname}
                          </Typography>
                        </Box>
                      )
                    ) : (
                      <Typography
                        variant="h6"
                        color="text.primary"
                        sx={{ display: "block", fontWeight: 600 }}
                      >
                        {task.flownodename || task.name}
                      </Typography>
                    )}
                    {/* Subtitle line: description (historyMode) or processmodelname (normal) */}
                    {historyMode ? (
                      editingDescription ? (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                          <TextField
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            variant="standard"
                            size="small"
                            autoFocus
                            sx={{ flex: 1 }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveDescription();
                              if (e.key === "Escape") cancelEditDescription();
                            }}
                            onBlur={() => {
                              if (editedDescription.trim() !== task?.description) saveDescription();
                              else setEditingDescription(false);
                            }}
                            disabled={saving}
                          />
                          <IconButton size="small" onClick={saveDescription} disabled={saving}>
                            <Check fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditDescription} disabled={saving}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Box
                          onClick={canEditProcessDetails ? startEditDescription : undefined}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25,
                            cursor: canEditProcessDetails ? 'pointer' : 'default',
                            '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                            transition: 'border-color 0.15s, background-color 0.15s',
                            display: 'inline-block',
                          }}
                        >
                          <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700 }}>
                            {task.description}
                          </Typography>
                        </Box>
                      )
                    ) : editingProcessName ? (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <TextField
                          value={editedProcessName}
                          onChange={(e) => setEditedProcessName(e.target.value)}
                          variant="standard"
                          size="small"
                          autoFocus
                          sx={{ flex: 1 }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveProcessName();
                            if (e.key === "Escape") cancelEditProcessName();
                          }}
                          onBlur={() => {
                            if (editedProcessName.trim() && editedProcessName.trim() !== task?.processmodelname) saveProcessName();
                            else setEditingProcessName(false);
                          }}
                          disabled={saving}
                        />
                        <IconButton size="small" onClick={saveProcessName} disabled={saving}>
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={cancelEditProcessName} disabled={saving}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Box
                        onClick={canEditProcessDetails ? startEditProcessName : undefined}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 1,
                          py: 0.25,
                          cursor: canEditProcessDetails ? 'pointer' : 'default',
                          '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                          transition: 'border-color 0.15s, background-color 0.15s',
                          display: 'inline-block',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {task.processmodelname}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      maxWidth: "100%",
                      overflowX: isMobile ? "auto" : "visible",
                      overflowY: "hidden",
                      flexWrap: "nowrap",
                      pb: isMobile ? 0.5 : 0,
                      "& > *": { flexShrink: 0 },
                      "& .MuiButton-root": { whiteSpace: "nowrap" },
                    }}
                  >
                    <FlowentiButton
                      id="btn-download-task-detail-document"
                      size="small"
                      variant="outlined"
                      startIcon={<SaveAlt />}
                      disabled={downloadingDocument}
                      onClick={handleDownloadTaskDocument}
                      sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                    >
                      Download
                    </FlowentiButton>
                    <FlowentiButton
                      id="btn-preview-task-detail-document"
                      size="small"
                      variant="outlined"
                      startIcon={<Print />}
                      disabled={downloadingDocument}
                      onClick={handlePreviewTaskDocument}
                      sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                    >
                      {getTranslation(translations, "generic.print")}
                    </FlowentiButton>
                    {isMobile && (
                      <FlowentiButton
                        id="btn-share-task-detail-document"
                        size="small"
                        variant="outlined"
                        startIcon={<Share />}
                        disabled={downloadingDocument}
                        onClick={handleShareTaskDocument}
                        sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                      >
                        {getTranslation(translations, "generic.share") || "Share"}
                      </FlowentiButton>
                    )}
                    <Labels labelCompleteMode labels={getLabelDataFromJsonTags(task?.jsontag)} />
                  </Stack>
                </Stack>
              )}
            </Box>
          </DialogHeader>

          {/* Action buttons bar */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: "background.paper",
              borderBottom: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap={isMobile ? "nowrap" : "wrap"}
              sx={{
                maxWidth: "100%",
                overflowX: isMobile ? "auto" : "visible",
                overflowY: "hidden",
                pb: isMobile ? 0.5 : 0,
                "& > *": { flexShrink: 0 },
                "& .MuiButton-root": { whiteSpace: "nowrap" },
              }}
            >
              {/* Priority */}
              {selectedPriority && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getTranslation(translations, "generic.priorities.caption")}:
                  </Typography>
                  {doing && !readonly ? (
                    <Select
                      value={selectedPriority.id}
                      onChange={handlePriorityChange}
                      displayEmpty
                      size="small"
                      sx={{
                        minWidth: 100,
                        height: 32,
                        "& .MuiSelect-select": { py: 0.5, px: 1 },
                      }}
                    >
                      {allPriorities.map((priority) => (
                        <MenuItem key={priority.id} value={priority.id}>
                          {priority.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Chip
                      label={selectedPriority.name}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              )}

              {/* Action buttons - visible only when doing and not readonly */}
              {!readonly && doing && (
                <>

                  <FlowentiButton
                    startIcon={<LabelOutlined color="primary" />}
                    size="small"
                    variant="outlined"
                    onClick={(e) => setIsLabelManagerShown(e.currentTarget)}
                  >
                    {getTranslation(translations, "generic.labels.caption")}
                  </FlowentiButton>
                  {checkLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <FlowentiButton
                        id="basic-button"
                        aria-controls={menuOpen ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={menuOpen ? "true" : undefined}
                        onClick={handleClick}
                        size="small"
                        variant="outlined"
                        startIcon={<AddToDrive color="primary" />}
                      >
                        {getTranslation(translations, "pm.startprocess.button.uploadfile.drive")}
                      </FlowentiButton>

                      <FlowentiButton
                        id="addfield-button"
                        startIcon={<AddCircleOutlineRounded color="primary" />}
                        size="small"
                        variant="outlined"
                        onClick={() => setOpenAddFieldDialog(true)}
                      >
                        {getTranslation(translations, "pm.mayactivities.addfield.label")}
                      </FlowentiButton>


                    </>
                  )}
                </>
              )}
              <ProcessNavigationMenu
                task={task}
                handleNextStep={handleNextStep}
                handleBackStep={handleBackStep}
                handleSequenceFlowChoice={handleSequenceFlowChoice}
                nextButtonLabel={nextButtonLabel}
                undoButtonLabel={undoButtonLabel}
                onClose={onClose}
              />

              <Box sx={{ flex: 1 }} />

              {/* History switch - always visible */}

              <ToggleButtonGroup
                value={activeView}
                exclusive
                onChange={(event, newView) => {
                  if (newView !== null) {
                    setActiveView(newView);
                    setShowHistory(newView === 'histories');
                  }
                }}
                size="small"
                aria-label="view selection"
              >
                <Tooltip title={getTranslation(translations, "settings.comments.label")}>
                  <ToggleButton
                    value="comments"
                    aria-label="comments"
                  >
                    <Comment color="primary" />
                  </ToggleButton>

                </Tooltip>
                <Tooltip title={getTranslation(translations, "settings.histories.label")}>
                  <ToggleButton value="histories" aria-label="histories">
                    <History color="primary" />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </Stack>

            {/* Error alerts */}
            {!readonly && doing && (
              <>
                {((storageDiskCanUse === false && !bypassSubscription) || uploadAttachmentError) && (
                  <Alert
                    sx={{ mt: 1, py: 0.5 }}
                    severity="error"
                    action={
                      <Button
                        size="small"
                        onClick={() => navigate(`/dashboard/organizations/${organizationId}/billing`, {
                          state: { organizationName: selectedOrganization?.name }
                        })}
                      >
                        {getTranslation(translations, "generic.upgrade")}
                      </Button>
                    }
                  >
                    {getTranslation(translations, "storage.limit.reached")}
                  </Alert>
                )}

                {maxAttachmentsError && (
                  <Alert
                    id="max-attachments-error"
                    sx={{ mt: 1, py: 0.5 }}
                    severity="error"
                  >
                    {getTranslation(translations, "attachment.limit.reached")}
                  </Alert>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Main content area */}
        <DialogContent
          sx={{
            p: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: { xs: "auto", md: "hidden" },
          }}
        >
          {isMobile ? (
            <Stack spacing={2} sx={{ flex: 1, overflow: "auto" }}>
              {/* LEFT content on mobile */}
              <Box sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  {/* Process name */}
                  {(task?.description || canEditProcessDetails) && (
                    <Box>
                      <Typography variant="h6" mb={1}>
                        {getTranslation(translations, "pm.processstart.processname.label") || "Nome processo"}
                      </Typography>
                      {!historyMode && task?.description && !editingDescription ? (
                        <Box
                          onClick={canEditProcessDetails ? startEditDescription : undefined}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            cursor: canEditProcessDetails ? 'pointer' : 'default',
                            '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                            transition: 'border-color 0.15s, background-color 0.15s',
                          }}
                        >
                          <Typography variant="body1">{task.description}</Typography>
                        </Box>
                      ) : !historyMode && editingDescription ? (
                        <TextField
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          variant="outlined"
                          size="small"
                          autoFocus
                          fullWidth
                          InputProps={{ sx: { borderRadius: 5 } }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveDescription();
                            if (e.key === "Escape") cancelEditDescription();
                          }}
                          onBlur={() => {
                            if (editedDescription.trim() !== task?.description) saveDescription();
                            else setEditingDescription(false);
                          }}
                          disabled={saving}
                        />
                      ) : historyMode && task?.description ? (
                        <Typography variant="body1">
                          {task.description}
                        </Typography>
                      ) : null}
                    </Box>
                  )}

                  {/* Rich text description */}
                  {(descriptionDetail || canEditProcessDetails) && (
                    <Box>
                      <Typography variant="h6" mb={1}>
                        {getTranslation(translations, "pm.startprocess.description") || "Descrizione dettagliata"}
                      </Typography>
                      <RichTextEditor
                        ref={descriptionDetailRef}
                        value={descriptionDetail || ""}
                        readOnly={!canEditProcessDetails}
                        minHeight={80}
                        onBlur={() => { saveDescriptionDetail(); }}
                        sx={{
                          width: "100%",
                          "& [class*='_root_']": { fontSize: "0.9375rem" },
                          "& [contenteditable]": { fontSize: "0.9375rem" },
                          "& [contenteditable] h1": { fontSize: "1.4rem" },
                          "& [contenteditable] h2": { fontSize: "1.2rem" },
                          "& [contenteditable] h3": { fontSize: "1.05rem" },
                          "& [contenteditable] table": { fontSize: "0.9375rem" },
                          "& .cm-editor": { fontSize: "0.9375rem" },
                        }}
                      />
                    </Box>
                  )}

                  {/* Fields */}
                  <Paper sx={{ bgcolor: "white", flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }} elevation={0}>
                    {Object.keys(userdataEntries).length !== 0 ? (
                      <Accordion elevation={0} disableGutters defaultExpanded sx={{ '&:before': { display: 'none' }, boxShadow: 'none', '&.MuiAccordion-root': { border: 'none' }, '&.Mui-expanded': { margin: 0 } }}>
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                            {getTranslation(translations, "process_model.schema.title")}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <FieldsList
                            canManage={doing && !readonly}
                            schema={task?.userschema}
                            selectedProcess={task?.idprocess}
                            value={userdataEntries}
                            onChange={setUserDataEntries}
                            openAddFieldDialog={openAddFieldDialog}
                            onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                              handleSaveUserdata={async (payload) => {
                              try {
                                const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
                                sendSnack({ message: data.responsemessage, type: "success" });
                                refresh && refresh();
                              } catch { /* ignore */ }
                            }}
                            readonly={task?.state === 0}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        <FieldsList
                          canManage={doing && !readonly}
                          schema={task?.userschema}
                          selectedProcess={task?.idprocess}
                          value={userdataEntries}
                          onChange={setUserDataEntries}
                          openAddFieldDialog={openAddFieldDialog}
                          onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                            handleSaveUserdata={async (payload) => {
                            try {
                              const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
                              sendSnack({ message: data.responsemessage, type: "success" });
                              refresh && refresh();
                            } catch { /* ignore */ }
                          }}
                          readonly={task?.state === 0}
                        />
                      </Box>
                    )}
                    {(attachments.length > 0 || (doing && !readonly)) && (
                      <Accordion elevation={0} disableGutters defaultExpanded sx={{ '&:before': { display: 'none' }, boxShadow: 'none', '&.MuiAccordion-root': { border: 'none' }, '&.Mui-expanded': { margin: 0 } }}>
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                            {getTranslation(translations, "settings.attachments.label")} ({attachments.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: -3 }}>
                          {doing && !readonly && (
                            <DropZone
                              files={[]}
                              externalFilesCount={attachments.length}
                              maxFiles={10 - attachments.length}
                              onFileChange={async (files) => {
                                if (files.length > 0) await handleUploadFile(files);
                              }}
                            />
                          )}
                          <AttachmentList
                            canManage={doing && !readonly}
                            attachments={attachments}
                            knownWidth={0}
                            getAttachments={getAttachments}
                            images={images}
                            histories={histories}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Paper>
                </Stack>
              </Box>

              {/* RIGHT content on mobile */}
              <Stack spacing={2}>
                <Paper sx={{ width: "100%", bgcolor: "#f0f0f0", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 300 }} elevation={0}>
                  <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.1)", flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                      {!showHistory
                        ? getTranslation(translations, "settings.comments.label")
                        : getTranslation(translations, "settings.histories.label")
                      }
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
                    {!showHistory ? (
                      <CommentsSection canWrite={doing && !readonly} canEdit={doing && !readonly} onComment={onComment} onEdit={onEditComment} comments={comments} />
                    ) : (
                      <HistoriesSection history={history} />
                    )}
                  </Box>
                </Paper>
              </Stack>
            </Stack>
          ) : (
            <PanelGroup
              orientation="horizontal"
              style={{ flex: 1, overflow: "hidden", height: "100%" }}
              defaultLayout={loadLayout(LAYOUT_KEY)}
              onLayoutChanged={(layout) => saveLayout(LAYOUT_KEY, layout)}
            >
              {/* LEFT panel: description, fields, attachments */}
              <Panel
                id="taskdetail-left"
                defaultSize="60%"
                minSize="35%"
                style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: "0 12px 0 24px" }}
              >
                <Stack spacing={3}>
                  {/* Process name */}
                  {(task?.description || canEditProcessDetails) && (
                    <Box>
                      <Typography variant="h6" mb={2}>
                        {getTranslation(translations, "pm.processstart.processname.label") || "Nome processo"}
                      </Typography>
                      {!historyMode && task?.description && !editingDescription ? (
                        <Box
                          onClick={canEditProcessDetails ? startEditDescription : undefined}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            cursor: canEditProcessDetails ? 'pointer' : 'default',
                            '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                            transition: 'border-color 0.15s, background-color 0.15s',
                          }}
                        >
                          <Typography variant="body1">{task.description}</Typography>
                        </Box>
                      ) : !historyMode && editingDescription ? (
                        <TextField
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          variant="outlined"
                          size="small"
                          autoFocus
                          fullWidth
                          InputProps={{ sx: { borderRadius: 5 } }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveDescription();
                            if (e.key === "Escape") cancelEditDescription();
                          }}
                          onBlur={() => {
                            if (editedDescription.trim() !== task?.description) saveDescription();
                            else setEditingDescription(false);
                          }}
                          disabled={saving}
                        />
                      ) : historyMode && task?.description ? (
                        <Typography variant="body1">
                          {task.description}
                        </Typography>
                      ) : null}
                    </Box>
                  )}

                  {/* Rich text description */}
                  {(descriptionDetail || canEditProcessDetails) && (
                    <Box>
                      <Typography variant="h6" mb={2}>
                        {getTranslation(translations, "pm.startprocess.description") || "Descrizione dettagliata"}
                      </Typography>
                      <RichTextEditor
                        ref={descriptionDetailRef}
                        value={descriptionDetail || ""}
                        readOnly={!canEditProcessDetails}
                        minHeight={isDescriptionDetailFocused ? 180 : 120}
                        onFocus={() => setIsDescriptionDetailFocused(true)}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setIsDescriptionDetailFocused(false);
                            saveDescriptionDetail();
                          }
                        }}
                        sx={{
                          width: "100%",
                          "& [class*='_root_']": { fontSize: "0.9375rem" },
                          "& [contenteditable]": { fontSize: "0.9375rem" },
                          "& [contenteditable] h1": { fontSize: "1.4rem" },
                          "& [contenteditable] h2": { fontSize: "1.2rem" },
                          "& [contenteditable] h3": { fontSize: "1.05rem" },
                          "& [contenteditable] table": { fontSize: "0.9375rem" },
                          "& .cm-editor": { fontSize: "0.9375rem" },
                        }}
                      />
                    </Box>
                  )}

                  {/* Fields - scrollable */}
                  <Paper
                    sx={{
                      bgcolor: "white",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "auto",
                    }}
                    elevation={0}
                  >
                    {Object.keys(userdataEntries).length !== 0 ? (
                      <Accordion
                        elevation={0}
                        disableGutters
                        defaultExpanded
                        sx={{
                          '&:before': {
                            display: 'none',
                          },
                          boxShadow: 'none',
                          '&.MuiAccordion-root': {
                            border: 'none',
                          },
                          '&.Mui-expanded': {
                            margin: 0,
                          },
                        }}
                      >
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                            {getTranslation(translations, "process_model.schema.title")}
                          </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                          <FieldsList
                            canManage={doing && !readonly}
                            schema={task?.userschema}
                            selectedProcess={task?.idprocess}
                            value={userdataEntries}
                            onChange={setUserDataEntries}
                            openAddFieldDialog={openAddFieldDialog}
                            onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                              handleSaveUserdata={async (payload) => {
                              try {
                                const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
                                sendSnack({ message: data.responsemessage, type: "success" });
                                refresh && refresh();
                              } catch {
                                // ignore
                              }
                            }}
                            readonly={task?.state === 0}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        <FieldsList
                          canManage={doing && !readonly}
                          schema={task?.userschema}
                          selectedProcess={task?.idprocess}
                          value={userdataEntries}
                          onChange={setUserDataEntries}
                          openAddFieldDialog={openAddFieldDialog}
                          onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                            handleSaveUserdata={async (payload) => {
                            try {
                              const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
                              sendSnack({ message: data.responsemessage, type: "success" });
                              refresh && refresh();
                            } catch {
                              // ignore
                            }
                          }}
                          readonly={task?.state === 0}
                        />
                      </Box>
                    )}
                    {/* Attachments accordion - shown when there are attachments OR when user can add */}
                    {(attachments.length > 0 || (doing && !readonly)) && (
                      <Accordion
                        elevation={0}
                        disableGutters
                        defaultExpanded
                        sx={{
                          '&:before': {
                            display: 'none',
                          },
                          boxShadow: 'none',
                          '&.MuiAccordion-root': {
                            border: 'none',
                          },
                          '&.Mui-expanded': {
                            margin: 0,
                          },
                        }}

                      >
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                            {getTranslation(translations, "settings.attachments.label")} ({attachments.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{ mt: -3 }}
                        >
                          {doing && !readonly && (
                            <DropZone
                              files={[]}
                              externalFilesCount={attachments.length}
                              maxFiles={10 - attachments.length}
                              subscriptionFeatures={{
                                bypassSubscription,
                                storageDiskCanUse,
                                refreshSubscriptionFeatures,
                                storageUsage,
                                checkLoading,
                              }}
                              onFileChange={async (files) => {
                                if (files.length > 0) {
                                  await handleUploadFile(files);
                                }
                              }}
                            />
                          )}
                          <AttachmentList
                            canManage={doing && !readonly}
                            attachments={attachments}
                            knownWidth={0}
                            getAttachments={getAttachments}
                            images={images}
                            histories={histories}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Paper>
                </Stack>
              </Panel>

              <PanelResizeHandle className="panel-resize-handle" style={{
                width: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "col-resize",
                transition: "background 0.15s",
              }}>
                <span style={{
                  width: 6,
                  height: 38,
                  borderRadius: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  backgroundColor: "rgba(0,0,0,0.12)",
                  padding: "6px 0",
                  transition: "background-color 0.15s",
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.3)" }} />
                  <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.3)" }} />
                  <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.3)" }} />
                </span>
              </PanelResizeHandle>

              {/* RIGHT panel: Comments / History */}
              <Panel
                id="taskdetail-right"
                defaultSize="40%"
                minSize="25%"
                style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                <Paper
                  sx={{
                    width: "100%",
                    bgcolor: "#f0f0f0",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    height: "100%",
                  }}
                  elevation={0}
                >
                  <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.1)", flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                      {!showHistory
                        ? getTranslation(translations, "settings.comments.label")
                        : getTranslation(translations, "settings.histories.label")
                      }
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
                    {!showHistory ? (
                      <CommentsSection
                        canWrite={doing && !readonly}
                        canEdit={doing && !readonly}
                        onComment={onComment}
                        onEdit={onEditComment}
                        comments={comments}
                      />
                    ) : (
                      <HistoriesSection
                        history={history}
                      />
                    )}
                  </Box>
                </Paper>
              </Panel>
            </PanelGroup>
          )}
        </DialogContent>

        {/* Google Drive Menu */}
        <GoogleDriveMenu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onRefreshAttachments={getAttachments}
          task={task}
          organizationId={organizationId}
          userLocale={user?.i18n}
        />

        {
          isLabelManagerShown && (
            <LabelManager
              idprocess={task?.idprocess}
              open={isLabelManagerShown}
              handleClose={handleLabelManagerClose}
            />
          )
        }
        <ConfirmDialog
          open={fullContentDialog.open}
          onClose={closeFullContentDialog}
          onConfirm={closeFullContentDialog}
          //  cancelText={getTranslation(translations, "generic.button.cancel")}
          confirmText={getTranslation(translations, "pm.minimap.close.lable")}
          title={task?.description || fullContentDialog.title}
          maxWidth="lg"
          fullWidth={true}
          content={
            <Box sx={{ whiteSpace: "pre-wrap", maxHeight: "600px", minHeight: "300px" }}>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fullContentDialog.content) }} />
            </Box>
          }
        />
      </Dialog >

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            height: isMobile ? "100%" : "85vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogHeader onClose={closePreview}>
          <Typography variant="h6" sx={{ px: 2 }}>
            {task?.flownodename || task?.name || "Preview"}
          </Typography>
        </DialogHeader>
        <DialogContent sx={{ p: 0, flex: 1, overflow: isMobile ? "auto" : "hidden" }}>
          {isMobile ? renderMobileTaskDocumentPreview() : previewPdfUrl && (
            <iframe
              src={previewPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaskDetailDialog;
